import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../../../core/services/settings.service';
import { ApplicationSetting } from '../../../../core/models/settings/setting.model';
import { LoadingComponent } from '../../../../shared/components/loading/loading.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-application-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingComponent, TranslateModule],
  templateUrl: './application-settings.component.html',
  styleUrl: './application-settings.component.scss'
})
export class ApplicationSettingsComponent implements OnInit {
  settings: ApplicationSetting[] = [];
  originalSettings: ApplicationSetting[] = [];
  changedSettings: ApplicationSetting[] = [];
  loading = false;
  error: string | null = null;
  success: string | null = null;
  showConfirmModal = false;
  editingKeys: Set<string> = new Set();

  constructor(
    private settingsService: SettingsService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  loadSettings(): void {
    this.loading = true;
    this.error = null;
    this.success = null;
    this.changedSettings = [];
    this.editingKeys.clear();

    this.settingsService.getSettings().subscribe({
      next: (data) => {
        this.settings = data.map(setting => ({ ...setting }));
        this.originalSettings = data.map(setting => ({ ...setting }));
        this.loading = false;
      },
      error: (err) => {
        this.error = this.translateService.instant('PIGLET.APPLICATION_SETTINGS.MESSAGES.LOAD_ERROR');
        this.loading = false;
        console.error(err);
      }
    });
  }

  startEditing(setting: ApplicationSetting): void {
    // For sensitive data, we need to fetch the actual value
    this.error = null;
    this.success = null;

    if (setting.isSensitiveData && !this.editingKeys.has(setting.key)) {
      this.loading = true;
      this.settingsService.getSettingByKey(setting.key).subscribe({
        next: (data) => {
          // Find the setting in the array and update its value
          const index = this.settings.findIndex(s => s.key === setting.key);
          if (index !== -1) {
            this.settings[index].value = data.value;
          }
          this.editingKeys.add(setting.key);
          this.loading = false;
        },
        error: (err) => {
          this.error = this.translateService.instant('PIGLET.APPLICATION_SETTINGS.MESSAGES.LOAD_ERROR');
          this.loading = false;
          console.error(err);
        }
      });
    } else {
      this.editingKeys.add(setting.key);
    }
  }

  onSettingChange(setting: ApplicationSetting): void {
    // Check if this setting has changed from its original value
    const originalSetting = this.originalSettings.find(s => s.key === setting.key);
    if (!originalSetting) return;

    const hasChanged =
      setting.value !== originalSetting.value ||
      setting.isSensitiveData !== originalSetting.isSensitiveData;

    // Update the changedSettings array
    if (hasChanged) {
      // Remove if it already exists
      this.changedSettings = this.changedSettings.filter(s => s.key !== setting.key);
      // Add the changed setting
      this.changedSettings.push({ ...setting });
    } else {
      // Remove from changed settings if it's back to original
      this.changedSettings = this.changedSettings.filter(s => s.key !== setting.key);
    }
  }

  isSettingChanged(key: string): boolean {
    return this.changedSettings.some(setting => setting.key === key);
  }

  discardChanges(): void {
    this.settings = this.originalSettings.map(setting => ({ ...setting }));
    this.changedSettings = [];
    this.editingKeys.clear();
    this.error = null;
    this.success = null;
  }

  openConfirmModal(): void {
    if (this.changedSettings.length === 0) {
      this.success = this.translateService.instant('PIGLET.APPLICATION_SETTINGS.MESSAGES.NO_CHANGES');
      return;
    }
    this.showConfirmModal = true;
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }

  saveChanges(): void {
    if (this.changedSettings.length === 0) {
      this.success = this.translateService.instant('PIGLET.APPLICATION_SETTINGS.MESSAGES.NO_CHANGES');
      return;
    }

    this.loading = true;
    this.error = null;
    this.success = null;

    this.settingsService.updateSettings(this.changedSettings).subscribe({
      next: () => {
        this.success = this.translateService.instant('PIGLET.APPLICATION_SETTINGS.MESSAGES.UPDATE_SUCCESS');
        this.loading = false;
        this.showConfirmModal = false;
        this.loadSettings();
      },
      error: (err) => {
        this.error = this.translateService.instant('PIGLET.APPLICATION_SETTINGS.MESSAGES.UPDATE_ERROR');
        this.loading = false;
        this.showConfirmModal = false;
        console.error(err);
      }
    });
  }
}
